import type {
  AnchoredRow,
  AnchoredRowRetiredMaterialDecision,
  RetiredMaterialDisposition,
  UnitMapping,
} from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import type { LedgerSafeRowEditResult } from './ledgerSafeRowEditing'

export type LedgerSafeRetiredMaterialKind = AnchoredRowRetiredMaterialDecision['kind']

export type LedgerSafeRetiredMaterialStatus = 'review' | RetiredMaterialDisposition

export interface LedgerSafeRetiredMaterialIssue {
  rowId: string
  rowLabelNumber: number
  rowPosition: number
  witnessId: string
  witnessSigla: string
  unitUuid: string
  unitLabel: string
  kind: LedgerSafeRetiredMaterialKind
  reason: string
  snapshotText: string
  legacyText: string
  status: LedgerSafeRetiredMaterialStatus
  isOnlyMappingInRow: boolean
}

export interface LedgerSafeEmptyReadingRowIssue {
  rowId: string
  rowLabelNumber: number
  rowPosition: number
  hasRowTitle: boolean
  sceneTitle: string
  status: 'review' | 'retained'
}

export interface LedgerSafeRetiredMaterialAudit {
  issues: LedgerSafeRetiredMaterialIssue[]
  emptyRows: LedgerSafeEmptyReadingRowIssue[]
  issueByCellKey: Map<string, LedgerSafeRetiredMaterialIssue>
  affectedRowIds: Set<string>
  unresolvedCount: number
}

interface RetiredMaterialWitness {
  id: string
  sigla?: string
  segmentation_result?: CompleteMicroSegmentationResult | null
  segmentation_runs?: Record<string, { result?: CompleteMicroSegmentationResult | null }>
  active_segmentation_run?: string
}

interface RetiredReplacementMetadata {
  ignored?: boolean
  ignore_reason?: string | null
  artifact_only?: boolean
  metadata_only?: boolean
  metadata_kind?: string | null
}

const resolveActiveSegmentationResult = (
  witness: RetiredMaterialWitness,
): CompleteMicroSegmentationResult | null => {
  const activeRunId = witness.active_segmentation_run
  if (activeRunId && witness.segmentation_runs?.[activeRunId]?.result) {
    return witness.segmentation_runs[activeRunId].result || null
  }
  return witness.segmentation_result || null
}

const getReplacementMetadata = (unit: MicroUnit): RetiredReplacementMetadata | null => {
  const candidate = unit.processing_metadata?.source_text_replacement
  return candidate && typeof candidate === 'object'
    ? candidate as RetiredReplacementMetadata
    : null
}

export const getLedgerSafeRetiredMaterialKind = (
  unit: MicroUnit | null | undefined,
): LedgerSafeRetiredMaterialKind | null => {
  if (!unit) return null
  const replacement = getReplacementMetadata(unit)
  if (!replacement) return null
  if (replacement.ignored) return 'ignored'
  if (replacement.artifact_only) return 'artifact-only'
  if (replacement.metadata_only) return 'metadata-only'
  return null
}

export const isLedgerSafeRetiredSourceUnit = (
  unit: MicroUnit | null | undefined,
): boolean => getLedgerSafeRetiredMaterialKind(unit) !== null

const getRetiredMaterialReason = (
  unit: MicroUnit,
  kind: LedgerSafeRetiredMaterialKind,
): string => {
  const replacement = getReplacementMetadata(unit)
  if (replacement?.ignore_reason) return replacement.ignore_reason
  if (kind === 'artifact-only') return 'Parsed source artifact'
  if (kind === 'metadata-only') {
    return replacement?.metadata_kind
      ? `Parsed ${replacement.metadata_kind}`
      : 'Parsed source metadata'
  }
  return 'Ignored during source replacement'
}

const getUnitIdentity = (unit: MicroUnit): string =>
  String(unit.unit_uuid || unit.unit_id || '').trim()

const getLegacyUnitText = (unit: MicroUnit): string =>
  String((unit as MicroUnit & { unit_text?: string }).unit_text || '')

const buildRetiredUnitIndex = (
  witnesses: RetiredMaterialWitness[],
): Map<string, { witness: RetiredMaterialWitness; unit: MicroUnit; kind: LedgerSafeRetiredMaterialKind }> => {
  const index = new Map<string, { witness: RetiredMaterialWitness; unit: MicroUnit; kind: LedgerSafeRetiredMaterialKind }>()
  witnesses.forEach(witness => {
    const result = resolveActiveSegmentationResult(witness)
    result?.chunk_results?.forEach(chunk => {
      ;(chunk.micro_units || []).forEach(unit => {
        const unitUuid = getUnitIdentity(unit)
        const kind = getLedgerSafeRetiredMaterialKind(unit)
        if (unitUuid && kind) {
          index.set(`${witness.id}:${unitUuid}`, { witness, unit, kind })
        }
      })
    })
  })
  return index
}

const findDecision = (
  row: AnchoredRow,
  witnessId: string,
  unitUuid: string,
): AnchoredRowRetiredMaterialDecision | null => (
  row.metadata?.retiredMaterial?.find(decision => (
    decision.witnessId === witnessId && decision.unitUuid === unitUuid
  )) || null
)

const getRowTitlePresence = (row: AnchoredRow): boolean => Boolean(
  row.metadata?.rowTitle?.labels?.some(label => label.text.trim()),
)

export const buildLedgerSafeRetiredMaterialAudit = ({
  rows,
  witnesses,
}: {
  rows: AnchoredRow[]
  witnesses: RetiredMaterialWitness[]
}): LedgerSafeRetiredMaterialAudit => {
  const retiredUnits = buildRetiredUnitIndex(witnesses)
  const issues: LedgerSafeRetiredMaterialIssue[] = []
  const emptyRows: LedgerSafeEmptyReadingRowIssue[] = []
  const issueByCellKey = new Map<string, LedgerSafeRetiredMaterialIssue>()
  const affectedRowIds = new Set<string>()

  rows.forEach(row => {
    if (row.unitMappings.length === 0) {
      emptyRows.push({
        rowId: row.id,
        rowLabelNumber: row.labelNumber,
        rowPosition: row.position,
        hasRowTitle: getRowTitlePresence(row),
        sceneTitle: String(row.metadata?.scene?.title || ''),
        status: row.metadata?.emptyRowReview?.disposition === 'retained' ? 'retained' : 'review',
      })
      affectedRowIds.add(row.id)
    }

    row.unitMappings.forEach(mapping => {
      const retired = retiredUnits.get(`${mapping.witnessId}:${mapping.unitUuid}`)
      if (!retired) return
      const decision = findDecision(row, mapping.witnessId, mapping.unitUuid)
      // A pre-existing excluded flag is not a human decision: older imports
      // used it for several meanings. Only this review record can close the item.
      const status: LedgerSafeRetiredMaterialStatus = decision?.disposition || 'review'
      const issue: LedgerSafeRetiredMaterialIssue = {
        rowId: row.id,
        rowLabelNumber: row.labelNumber,
        rowPosition: row.position,
        witnessId: mapping.witnessId,
        witnessSigla: retired.witness.sigla || mapping.witnessId,
        unitUuid: mapping.unitUuid,
        unitLabel: mapping.unitLabel || retired.unit.unit_label || mapping.unitUuid,
        kind: retired.kind,
        reason: decision?.reason || getRetiredMaterialReason(retired.unit, retired.kind),
        snapshotText: String(mapping.textSnapshot || ''),
        legacyText: getLegacyUnitText(retired.unit),
        status,
        isOnlyMappingInRow: row.unitMappings.length === 1,
      }
      issues.push(issue)
      issueByCellKey.set(`${row.id}:${mapping.witnessId}`, issue)
      affectedRowIds.add(row.id)
    })
  })

  issues.sort((left, right) => (
    left.rowPosition - right.rowPosition
    || left.witnessSigla.localeCompare(right.witnessSigla)
  ))
  emptyRows.sort((left, right) => left.rowPosition - right.rowPosition)

  return {
    issues,
    emptyRows,
    issueByCellKey,
    affectedRowIds,
    unresolvedCount: issues.filter(issue => issue.status === 'review').length
      + emptyRows.filter(issue => issue.status === 'review').length,
  }
}

const replaceDecision = ({
  row,
  mapping,
  disposition,
  kind,
  reason,
  decidedAt,
}: {
  row: AnchoredRow
  mapping: UnitMapping
  disposition: RetiredMaterialDisposition
  kind: LedgerSafeRetiredMaterialKind
  reason: string
  decidedAt: string
}): AnchoredRowRetiredMaterialDecision[] => [
  ...(row.metadata?.retiredMaterial || []).filter(decision => !(
    decision.witnessId === mapping.witnessId && decision.unitUuid === mapping.unitUuid
  )),
  {
    witnessId: mapping.witnessId,
    unitUuid: mapping.unitUuid,
    disposition,
    kind,
    reason,
    decidedAt,
  },
]

export const applyLedgerSafeRetiredMaterialDisposition = ({
  rows,
  rowId,
  witnessId,
  unitUuid,
  disposition,
  kind,
  reason,
  decidedAt = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  rowId: string
  witnessId: string
  unitUuid: string
  disposition: RetiredMaterialDisposition
  kind: LedgerSafeRetiredMaterialKind
  reason: string
  decidedAt?: string
}): LedgerSafeRowEditResult => {
  const row = rows.find(candidate => candidate.id === rowId)
  if (!row) throw new Error('Retired-material row was not found')
  const mapping = row.unitMappings.find(candidate => (
    candidate.witnessId === witnessId && candidate.unitUuid === unitUuid
  ))
  if (!mapping) throw new Error('Retired-material cell was not found')

  const updatedRows = rows.map(candidate => candidate.id !== rowId ? candidate : {
    ...candidate,
    unitMappings: candidate.unitMappings.map(current => (
      current.witnessId === witnessId && current.unitUuid === unitUuid
        ? { ...current, excluded: true }
        : current
    )),
    metadata: {
      ...(candidate.metadata || {}),
      retiredMaterial: replaceDecision({
        row: candidate,
        mapping,
        disposition,
        kind,
        reason,
        decidedAt,
      }),
    },
    status: candidate.status === 'confirmed' ? 'needs-review' as const : candidate.status,
    updatedAt: decidedAt,
  })

  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: { rowId, witnessId },
    requiresRelabel: false,
  }
}

export const applyLedgerSafeDetachRetiredMaterial = ({
  rows,
  rowId,
  witnessId,
  unitUuid,
  detachedAt = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  rowId: string
  witnessId: string
  unitUuid: string
  detachedAt?: string
}): LedgerSafeRowEditResult => {
  const row = rows.find(candidate => candidate.id === rowId)
  if (!row) throw new Error('Retired-material row was not found')
  if (!row.unitMappings.some(mapping => mapping.witnessId === witnessId && mapping.unitUuid === unitUuid)) {
    throw new Error('Retired-material cell was not found')
  }

  const updatedRows = rows.map(candidate => {
    if (candidate.id !== rowId) return candidate
    const remainingDecisions = (candidate.metadata?.retiredMaterial || []).filter(decision => !(
      decision.witnessId === witnessId && decision.unitUuid === unitUuid
    ))
    const metadata = { ...(candidate.metadata || {}) }
    if (remainingDecisions.length > 0) {
      metadata.retiredMaterial = remainingDecisions
    } else {
      delete metadata.retiredMaterial
    }
    return {
      ...candidate,
      unitMappings: candidate.unitMappings.filter(mapping => !(
        mapping.witnessId === witnessId && mapping.unitUuid === unitUuid
      )),
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
      status: candidate.status === 'confirmed' ? 'needs-review' as const : candidate.status,
      updatedAt: detachedAt,
    }
  })

  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: { rowId, witnessId },
    requiresRelabel: false,
  }
}

export const deleteLedgerSafeEmptyRowAfterReview = ({
  rows,
  rowId,
  selectedWitnessId,
}: {
  rows: AnchoredRow[]
  rowId: string
  selectedWitnessId: string
}): LedgerSafeRowEditResult => {
  const rowIndex = rows.findIndex(row => row.id === rowId)
  if (rowIndex < 0) throw new Error('Empty row was not found')
  if (rows[rowIndex].unitMappings.length > 0) {
    throw new Error('Detach or hide every reading before deleting this row')
  }
  if (rows.length <= 1) throw new Error('Cannot delete the only row in the collation')

  const updatedRows = rows.filter(row => row.id !== rowId)
  const selectedRow = updatedRows[Math.min(rowIndex, updatedRows.length - 1)]
  return {
    updatedRows,
    changedRowIds: [rowId],
    selectedCell: { rowId: selectedRow.id, witnessId: selectedWitnessId },
    requiresRelabel: true,
  }
}

export const applyLedgerSafeRetainEmptyRowAfterReview = ({
  rows,
  rowId,
  selectedWitnessId,
  decidedAt = new Date().toISOString(),
}: {
  rows: AnchoredRow[]
  rowId: string
  selectedWitnessId: string
  decidedAt?: string
}): LedgerSafeRowEditResult => {
  const row = rows.find(candidate => candidate.id === rowId)
  if (!row) throw new Error('Empty row was not found')
  if (row.unitMappings.length > 0) {
    throw new Error('This row now contains a reading and no longer needs empty-row review')
  }
  return {
    updatedRows: rows.map(candidate => candidate.id !== rowId ? candidate : {
      ...candidate,
      metadata: {
        ...(candidate.metadata || {}),
        emptyRowReview: {
          disposition: 'retained' as const,
          decidedAt,
        },
      },
      updatedAt: decidedAt,
    }),
    changedRowIds: [rowId],
    selectedCell: { rowId, witnessId: selectedWitnessId },
    requiresRelabel: false,
  }
}
